import { GroupedList, IGroup, IGroupHeaderProps, IRenderFunction, List, Spinner, SpinnerSize, Stack, StackItem, Text } from '@fluentui/react';
import React, { useEffect, useRef, useState, useContext } from 'react';
import { AppStateContext } from '../../state/AppProvider';
import { ChatHistoryListItemGroups } from './ChatHistoryListItem';
import { Conversation } from '../../api/models';
import { historyList } from '../../api';
import styles from "./ChatHistoryPanel.module.css"

interface ChatHistoryListProps {
    searchKeyword: string
}

export interface GroupedChatHistory {
    month: string;
    entries: Conversation[]
}

const groupByMonth = (entries: Conversation[], ragIds: Set<number>) => {
    const groups: GroupedChatHistory[] = [{ month: "お気に入り", entries: [] },{ month: "直近7日間", entries: [] }];
    const currentDate = new Date();
  
    entries.forEach((entry) => {
        const date = new Date(entry.date);
        const daysDifference = (currentDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
        const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' })
        const existingGroup = groups.find((group) => group.month === monthYear);
        if(ragIds.size == entry.rag_ids?.length && entry.rag_ids?.every((ragId) => ragIds.has(ragId))){
            // rag_idの一致するもののみ
            if(entry.is_favorite){
                groups[0].entries.push(entry);
            }else if(daysDifference <= 7){
                groups[1].entries.push(entry);
            }else{
                if (existingGroup) {
                    existingGroup.entries.push(entry);
                } else {
                    groups.push({ month: monthYear, entries: [entry] });
                }
            }
        }

    });

    groups.sort((a, b) => {
        // Check if either group has no entries and handle it
        if (a.entries.length === 0 && b.entries.length === 0) {
            return 0; // No change in order
        } else if (a.entries.length === 0) {
            return 1; // Move 'a' to a higher index (bottom)
        } else if (b.entries.length === 0) {
            return -1; // Move 'b' to a higher index (bottom)
        }
        const isFavoriteA = a.entries[0].is_favorite === undefined ? false : a.entries[0].is_favorite;
        const isFavoriteB = b.entries[0].is_favorite === undefined ? false : b.entries[0].is_favorite;
        if (isFavoriteA !== isFavoriteB) {
            return isFavoriteA ? -1 : 1;
        }
        const dateA = new Date(a.entries[0].date);
        const dateB = new Date(b.entries[0].date);
        return dateB.getTime() - dateA.getTime();
    });

    groups.forEach((group) => {
        group.entries.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB.getTime() - dateA.getTime();
        });
    });
  
    return groups;
};

const PAGE_SIZE = 25;

const ChatHistoryList: React.FC<ChatHistoryListProps> = (props: ChatHistoryListProps) => {
    const appStateContext = useContext(AppStateContext);
    const chatHistory = appStateContext?.state.chatHistory;
    const {searchKeyword} = props;
    const [showSpinner, setShowSpinner] = useState(false);
    // 監視対象のDOM。refではなくstateで持つ(refだと再マウントをeffectが検知できない)
    const [observerNode, setObserverNode] = useState<HTMLDivElement | null>(null);
    const [observerCounter, setObserverCounter] = useState(0);
    const initialFetched = useRef(false);
    // 進行中リクエストの世代。追い抜かれた古いレスポンスを破棄するために使う
    const requestIdRef = useRef(0);
    // 次に取得する開始位置。dispatch反映前のchatHistory.lengthは古いのでrefで持つ
    const offsetRef = useRef(0);
    // まだ続きがあるか
    const hasMoreRef = useRef(true);
    // 追加読み込みの多重発火防止
    const isFetchingRef = useRef(false);

    const groupedChatHistory = chatHistory && chatHistory.length > 0 ? groupByMonth(chatHistory, appStateContext.state.selectedRAGIds) : [];

    // RAGが変更れた、又はキーワードが変更されたため、履歴の表示を切り替える必要がある
    useEffect(() => {
        const ragIds = appStateContext?.state.selectedRAGIds;
        if (ragIds === undefined) return;

        // 取得条件が変わったので、追加読み込みの状態を先頭に巻き戻す
        // (ここでfalseにしないと、取り直しの最中に古いoffsetで追加読み込みが走る)
        initialFetched.current = false;
        offsetRef.current = 0;
        hasMoreRef.current = true;

        const fn = async () => {
            const applied = await handleFetchHistory(0, PAGE_SIZE, true, searchKeyword, ragIds)
            // 自分が最新のリクエストとして反映されたときだけ追加読み込みを解禁する
            if (applied) initialFetched.current = true
        }
        fn()
    }, [appStateContext?.state.selectedRAGIds, searchKeyword]);

    // スクロールが終端まで到達し、追加で読み込もうとする
    useEffect(() => {
        const ragIds = appStateContext?.state.selectedRAGIds;
        if (ragIds === undefined) return;
        // 初回取得が終わっていない(=条件変更で取り直し中)なら追加読み込みしない
        if (!initialFetched.current) return;
        if (!hasMoreRef.current) return;
        if (isFetchingRef.current) return;

        handleFetchHistory(offsetRef.current, PAGE_SIZE, false, searchKeyword, ragIds);
    }, [observerCounter]);

    useEffect(() => {
        // 検索結果0件などで監視対象が一度アンマウントされても、
        // 再マウント時にobserverNodeが更新されるのでobserverを張り直せる
        if (!observerNode) return;

        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting)
                    setObserverCounter((observerCounter) => observerCounter += 1);
            },
            { threshold: 0.5 }
        );

        observer.observe(observerNode);

        return () => {
            observer.disconnect();
        };
    }, [observerNode]);

    // 戻り値: このリクエストの結果をstateに反映したか(追い抜かれて破棄した場合はfalse)
    const handleFetchHistory = async (offset: number, length: number, replace: boolean = false, keyword: string, ragIds: Set<number>): Promise<boolean> => {
        // この呼び出しを最新世代として登録する。これより前のリクエストの結果は捨てる
        const requestId = ++requestIdRef.current;
        isFetchingRef.current = true;

        setShowSpinner(true);
        try{
            const response = await historyList(offset, length, Array.from(ragIds).sort(), keyword);

            // 待っている間に条件が変わって新しいリクエストが始まっていたら、
            // この結果は古い条件のものなので反映しない(意図しない上書きを防ぐ)
            if (requestId !== requestIdRef.current) return false;

            if (response) {
                hasMoreRef.current = response.length >= length;
                offsetRef.current = offset + response.length;
                if (replace) {
                    appStateContext?.dispatch({ type: 'FETCH_CHAT_HISTORY', payload: response });
                } else {
                    appStateContext?.dispatch({ type: 'APPEND_CHAT_HISTORY', payload: response });
                }
            } else {
                hasMoreRef.current = false;
                // 追加読み込みの失敗で表示中の履歴を消さない
                if (replace) appStateContext?.dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null });
            }
            return true;
        }
        finally {
            // 自分が最新のときだけ解除する(古いリクエストが新しい方のスピナーを消さないように)
            if (requestId === requestIdRef.current) {
                isFetchingRef.current = false;
                setShowSpinner(false);
            }
        }
    }
    if(chatHistory && chatHistory.length > 0){
        return (
            <Stack style={{width: "100%"}}>
                <ChatHistoryListItemGroups groupedChatHistory={groupedChatHistory}/>
                <div ref={setObserverNode} className='observetarget' style={{height: 10}} />
                {showSpinner && <div className={styles.spinnerContainer}>
                    <Spinner size={SpinnerSize.small} aria-label="loading more chat history" className={styles.spinner}/>
                </div>}
            </Stack>

        );
    }
    if (showSpinner) {
        return <Stack>
            <div className={styles.spinnerContainer}>
                <Spinner size={SpinnerSize.small} aria-label="loading more chat history" className={styles.spinner}/>
            </div>
        </Stack>;
    }
    return <Stack horizontal horizontalAlign='center' verticalAlign='center' style={{ width: "100%", marginTop: 10 }}>
        <StackItem>
            <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 14 }}>
                <span>チャット履歴がありません</span>
            </Text>
        </StackItem>
    </Stack>;
};

export default ChatHistoryList;
